<script setup>
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {ToastUtil} from "@/Utils/ToastUtil";
import {computed, onMounted, reactive, ref, watch} from "vue";

// Cookie key
const COOKIE_KEY = 'clientCollections_itemsPerPage'
const DEFAULT_PER_PAGE = 5

// Cookie helpers
const readCookie = (key, fallback) => {
  const match = document.cookie.match(new RegExp('(?:^|; )' + key + '=([^;]*)'))
  return match ? parseInt(decodeURIComponent(match[1]), 10) : fallback
}
const writeCookie = (key, value, days = 365) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  document.cookie = `${key}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`
}

// Initialise from cookie, fall back to default
const itemsPerPage = ref(readCookie(COOKIE_KEY, DEFAULT_PER_PAGE))

// Persist whenever the user changes it
watch(itemsPerPage, (val) => {
  writeCookie(COOKIE_KEY, val)
})

onMounted(() => {
  loadData()
})

const data = reactive({
  search: '',
  loading: false,
  headers: [
    {title: 'Company', key: 'company'},
    {title: 'Rep', key: 'rep'},
    {title: 'Claims', key: 'claimCount', align: 'end'},
    {title: 'Invoices', key: 'invoiceCount', align: 'end'},
    {title: 'Total Due', key: 'amountDue', align: 'end', value: (item) => currency(item.amountDue)},
    {title: 'Total Payments', key: 'payments', align: 'end', value: (item) => currency(item.payments)},
    {title: 'Total Balance', key: 'balance', align: 'end', value: (item) => currency(item.balance)},
  ],
  items: [],
})

const buildTableItems = (apiData) => {
  return apiData.map(company => {

    // get all the invoices for this company
    const invoices = company.invoices ?? []

    // Count unique claim IDs across all invoices for this company
    const uniqueClaimIds = new Set(invoices.map(i => i.internal_claim_id))

    // dollar totals values come back as formatted money strings e.g. "$405.00"
    // so we need to strip the $ and commas before parsing
    const parseMoney = (val) => parseFloat(String(val).replace(/[$,]/g, '')) || 0

    const amountDue = invoices.reduce((sum, i) => sum + parseMoney(i.amount), 0)
    const payments = invoices.reduce((sum, i) => sum + parseMoney(i.payments), 0)
    const balance = invoices.reduce((sum, i) => sum + parseMoney(i.balance), 0)

    return {
      company: company.company,
      rep: company.rep ?? 'Missing Rep',
      email: company.rep_email ?? '-',
      invoiceCount: invoices.length,
      claimCount: uniqueClaimIds.size,
      amountDue,
      payments,
      balance,
      invoices,
    }
  })
}

// after the data reactive block:
const filteredItems = computed(() => {
  const q = data.search.trim().toLowerCase()
  if (!q) return data.items
  return data.items.filter(i =>
      (i.company?.toLowerCase() ?? '').includes(q) ||
      (i.rep?.toLowerCase() ?? '').includes(q)
  )
})

const currency = (val) => {
  return Number(val).toLocaleString('en-US', {style: 'currency', currency: 'USD'})
}

const loadData = async () => {
  data.loading = true
  try {
    const response = await ApiWrapper.get('reports', 'get-client-collection-report')
    data.items = buildTableItems(response.data)
  } catch (e) {
    console.error('Failed to load client collections:', e)
  } finally {
    data.loading = false
  }
}

const mailtoLink = (item) => {
  try {
    if (!item.email) return null

    // Build the invoice breakdown lines
    const invoiceLines = item.invoices.map(inv => [
      `Claim:   ${inv.claim_number}`,
      `Invoice: ${inv.invoice_num}`,
      `Date:    ${inv.date_generated}`,
      `Amount:  ${inv.amount}`,
      `Balance: ${inv.balance}`,
    ].join('\r\n')).join('\r\n\r\n')

    const body = [
      `Hello ${item.rep},`,
      '',
      `I'm reaching out regarding the outstanding balance on your account with ${item.company}.`,
      '',
      'Account summary:',
      `  - Claims:           ${item.claimCount}`,
      `  - Invoices:         ${item.invoiceCount}`,
      `  - Total amount due: ${currency(item.amountDue)}`,
      `  - Total payments:   ${currency(item.payments)}`,
      `  - Balance:          ${currency(item.balance)}`,
      '',
      '--------------------',
      '',
      invoiceLines,
    ].join('\r\n')

    return `mailto:${item.email}` +
        `?subject=${encodeURIComponent(`Account Balance – ${item.company}`)}` +
        `&body=${encodeURIComponent(body)}`
  } catch (e) {
    console.error(`Error generating mailto link for ${item.company}:`, e)
  }
}

//-----------------------------------------------------------------------------
// Invoices sent claim notes Dialog

let dialog = ref(false);
let dialog_activator = ref(null);
let dialog_tableSelected = ref([]);
let dialog_clientRep = '';
let dialog_clientRepEmail = '';

//Modal dialog data-table model
let dialog_tblData = reactive({
  search: false,
  loading: false,
  headers: [
    {title: 'Claim', key:'claim_number', sortable: false },
    {title: 'Invoice', key: 'invoice_num', sortable: false },
    {title: 'Amount', key: 'amount', sortable: false }
  ],
  items: [],
})

//Tie dialog to specific data-table row link
const dialog_linkRegister = (event) => {
  dialog_activator.value = event.currentTarget;
}

//Load the Dialog's data table with the applicable clicked 'item' from component's main data table
const dialog_loadTableData = (item) => {
  dialog_tableSelected.value = [];
  dialog_tblData.items = item.invoices;
  dialog_tableSelected.value = dialog_tblData.items;
  dialog_clientRep = item.rep;
  dialog_clientRepEmail = item.email;
}

const SendAddNotesApi = async (data) => {
  try {
    await ApiWrapper.post('invoices', 'create-invoices-sent-note', data)
        .then( (res) => {
          ToastUtil.successMsg('Invoice Notes Generated', 3000);
        })
        .catch(e => {
          throw e;
        });
  } catch( err ) {
    console.error(`Failed to call API`);
    ToastUtil.errorMsg('Failed to generate notes', 5000);
  }
}

//Handler to verify data and perform api call
const createInvoiceSentNotes = async (invoices) => {
  if(!invoices || invoices.length === 0) {
    console.error('No invoices-list index id');
    ToastUtil.infoMsg('No invoices selected', 2500);
    return false;
  }

  let apiData = { rep: encodeURI(`${dialog_clientRep} (${dialog_clientRepEmail})`), invoices: [] }
  invoices.forEach( (item, idx, invs) => {
    apiData.invoices.push(item.invoice_num);
  });

  await SendAddNotesApi(apiData);
  dialog.value = false; //Hide dialog
}

</script>

<template>
  <v-container fluid>
    <v-dialog :activator="dialog_activator" v-model="dialog" max-width="800" max-height="600" scrollable persistent location="center center">
      <template v-slot:default="{ model: proxyModel }">
        <v-card title="Invoices Sent" subtitle="Uncheck any unsent invoices. Then press the 'Generate Notes' button to add collection notes.">
          <v-divider class="mt-3"></v-divider>
          <v-data-table
              :headers="dialog_tblData.headers"
              :items="dialog_tblData.items"
              item-value="invoice_num"
              v-model="dialog_tableSelected"
              density="compact"
              show-select
              disable-sort
              hide-default-footer
              return-object
          >
          </v-data-table>
          <template v-slot:actions>
            <v-btn variant="elevated" color="primary" elevation="3" text="Cancel" @click="dialog=false"></v-btn>
            <v-spacer></v-spacer>
            <v-btn variant="elevated" color="primary" elevation="3" text="Generate Notes" @click="createInvoiceSentNotes(dialog_tableSelected)"></v-btn>
          </template>
        </v-card>
      </template>
    </v-dialog>
    <v-card class="elevation-2">
      <v-toolbar density="comfortable" flat color="white" class="pb-4">
        <v-toolbar-title>Client Collections</v-toolbar-title>
        <v-progress-linear
            :active="data.loading"
            :indeterminate="data.loading"
            absolute
            bottom
            color="primary"
        />
        <v-divider class="mx-4" inset vertical/>
        <v-spacer/>
        <v-text-field
            v-model="data.search"
            label="Search"
            prepend-inner-icon="mdi-magnify"
            hint="Filter by company or rep name"
            persistent-hint
            density="compact"
            clearable
            class="px-4"
            style="max-width: 280px"
        />
      </v-toolbar>

      <v-data-table
          :headers="data.headers"
          :items="filteredItems"
          :loading="data.loading"
          density="compact"
          v-model:items-per-page="itemsPerPage"

          :items-per-page-options="[5, 10, 25, 50, 100, -1]"
          loading-text="Loading... Please wait"
      >
        <template #[`item.rep`]="{ item }">
          <a v-if="item.email" :href="mailtoLink(item)" target="_blank" @click="dialog_loadTableData(item)" @mouseenter="dialog_linkRegister($event)" class="text-primary text-decoration-none"> {{ item.rep }} </a>
          <span v-else class="d-flex align-center ga-2">
            {{ item.rep }}
            <v-chip size="x-small" color="error" variant="tonal">No email</v-chip>
          </span>
        </template>

        <template v-slot:top>
          <v-alert type="info" variant="outlined" text="Click on a Rep name to begin email."/>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>